import { RadioGroup } from '../../forms/radio-group/RadioGroup'
import { RadioButton } from '../../forms/radio-button/RadioButton'
import React, { useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { BpButton } from '../../buttons/bp-button/BpButton'
import { IconCheck } from '../../icons/IconCheck'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { hideModal, setModal, showAlert } from '../../../../app/store/reducers/AppSlice'
import { Preloader } from '../../cards/preloader/Preloader'
import { selectTariffDetails } from '../../../../app/store/reducers/AuthSlice'
import { IconCrown } from '../../icons/IconCrown'
import { ConfirmModal } from './ui/ConfirmModal'
import { Collapse } from 'antd'
import {
  RequestChangeTariffResponse,
  TariffPrecheck,
  useGetSiteCustomerReviewsQuery,
  useGetSiteFaqQuery,
  useGetTariffListQuery,
  usePostUserRequestChangeTariffMutation,
} from '../../../../app/api'
import useEmblaCarousel from 'embla-carousel-react'
import { IconArrowLeft } from '../../icons/IconArrowLeft'
import { IconArrowRight } from '../../icons/IconArrowRight'

import styles from './ChangeTariff.module.css'
import clsx from 'clsx'
import { QuestionsImage } from './ui/QuestionsImage'
import { ContactsModal } from './ui/ContactsModal'

const { Panel } = Collapse

export const CustomerReviews = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll: 'auto',
    loop: true,
  })

  const { data: customerReviews, isLoading } = useGetSiteCustomerReviewsQuery()

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const extendedCustomerReviews = useMemo(() => {
    if (!customerReviews?.data?.items || customerReviews.data.items.length === 0) {
      return []
    }

    const items = customerReviews.data.items
    const targetCount = 11

    if (items.length >= targetCount) {
      return items
    }

    const result = [...items]
    while (result.length < targetCount) {
      const remaining = targetCount - result.length
      const toAdd = items.slice(0, Math.min(remaining, items.length))
      result.push(...toAdd)
    }

    return result
  }, [customerReviews?.data?.items])

  if (isLoading || !customerReviews?.data?.items || customerReviews.data.items.length === 0) {
    return null
  }

  return (
    <div className={'relative mt-[42px]'}>
      <div ref={emblaRef} className={clsx(styles.embla)}>
        <div className={clsx(styles.emblaContainer)}>
          {extendedCustomerReviews.map((item: any, index: number) => (
            <div key={index} className={clsx(styles.emblaSlide)}>
              <div
                className={
                  'flex h-full w-full flex-col rounded-[16px] bg-white px-[24px] py-[16px]'
                }
              >
                <div className={'h-full text-[16px] text-focus-600'}>
                  <p>{item.review}</p>
                </div>
                <div className='flex flex-col gap-[16px] md:flex-row md:items-end'>
                  <div className={'order-2 pt-[16px] text-[16px] text-focus-600 md:order-1'}>
                    <p>{item.author}</p>
                    <p className={'text-[14px] text-aqua-300'}>{item.company}</p>
                  </div>
                  <div
                    className={
                      'order-1 flex h-[30px] w-[120px] items-end md:order-2 md:ml-auto md:h-[60px] md:justify-end'
                    }
                  >
                    {item.logo_url && (
                      <img
                        alt={item.company}
                        className={'max-h-full max-w-full'}
                        src={item.logo_url}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div
        className={clsx(
          styles.emblaControls,
          'pointer-events-none absolute inset-0 flex w-full items-center justify-between gap-[24px] px-[16px]',
        )}
      >
        <BpButton className={'pointer-events-auto !px-[11px]'} onClick={scrollPrev}>
          <IconArrowLeft className={'w-[12px]'} />
        </BpButton>
        <BpButton className={'pointer-events-auto !px-[11px]'} onClick={scrollNext}>
          <IconArrowRight className={'w-[12px]'} />
        </BpButton>
      </div>
    </div>
  )
}

export const Faq = () => {
  const dispatch = useAppDispatch()

  const { data: faq, isLoading } = useGetSiteFaqQuery()

  if (isLoading || !faq?.data?.items || faq.data.items.length === 0) {
    return null
  }

  return (
    <div className={'mx-auto max-w-[728px] px-[16px] py-[60px]'}>
      <p className={'pb-[16px] text-center text-[32px] font-medium'}>Frequently Asked Questions</p>
      <div>
        <Collapse
          expandIcon={({ isActive }) => (
            <span className='leading-1 !text-[32px] !font-light !text-focus-600'>
              {isActive ? '−' : '+'}
            </span>
          )}
          accordion={true}
          defaultActiveKey={[0]}
          expandIconPosition={'right'}
        >
          {faq.data.items.map((item: any, index: number) => (
            <Panel key={index} header={item.question}>
              {item.answer}
            </Panel>
          ))}
        </Collapse>
      </div>
      <div className={'flex flex-col items-center gap-[16px] pt-[60px]'}>
        <QuestionsImage />
        <div className={'flex flex-col items-center'}>
          <p className={'text-[20px] font-medium leading-[32px] text-focus-600'}>
            Have other questions?
          </p>
          <p className={'text-[16px] text-focus-600'}>Contact us—we&apos;re happy to help!</p>
        </div>
        <BpButton
          className={'uppercase'}
          size={'md'}
          onClick={() => dispatch(setModal({ children: <ContactsModal /> }))}
        >
          Contact Us
        </BpButton>
      </div>
    </div>
  )
}

export const ChangeTariff = () => {
  const { t } = useTranslation()
  const tariffDetails = useAppSelector(selectTariffDetails)
  const [period, setPeriod] = useState<'annually' | 'monthly'>('annually')
  const [selectedTariff, setSelectedTariff] = useState<{ id: number }>()
  const dispatch = useAppDispatch()

  const { data } = useGetTariffListQuery()
  const [requestChangeTariff, { isLoading: requestChangeTariffLoading }] =
    usePostUserRequestChangeTariffMutation()
  const [requestStripePrecheck, { isLoading: stripePrecheckLoading }] =
    usePostUserRequestChangeTariffMutation()

  const tariffs = useMemo(() => {
    if (data && Array.isArray(data?.data)) {
      const basic = [...data.data]
        .filter((tariff: any) => tariff.type === 'basic')
        .sort((a: any, b: any) => a.order - b.order)
        .slice(0, 2)
      const custom = [...data.data]
        .filter((tariff: any) => tariff.type === 'custom')
        .sort((a: any, b: any) => a.order - b.order)
        .slice(0, 1)

      return [...basic, ...custom]
    }
  }, [data])

  const handleChangeTariff = async (
    id: number,
    tariffName: string,
    period: 'annually' | 'monthly',
  ) => {
    const handleChangeTariff = () => {
      requestChangeTariff({
        requestChangeTariffForm: { tariff_id: id, payment_periodicity: period },
      })
        .unwrap()
        .then((response) => {
          if (!response.success) return

          const url = (response as RequestChangeTariffResponse).data?.session_url

          if (url) {
            window.location.href = url
          } else {
            dispatch(
              showAlert({
                message:
                  id && tariffDetails.tariff?.payment_method !== 'manual'
                    ? t('notifications.paymentSuccess')
                    : 'Your account manager will contact you shortly to upgrade your plan.',
                type: 'success',
                timeout: 5000,
              }),
            )
            dispatch(hideModal())
          }
        })
        .catch(() => undefined)
    }

    if (tariffDetails.tariff?.payment_method === 'manual') {
      handleChangeTariff()
    } else {
      requestStripePrecheck({
        requestChangeTariffForm: {
          tariff_id: id,
          payment_periodicity: period,
          stripe_precheck: true,
        },
      })
        .unwrap()
        .then((response) => {
          const precheck = (response as RequestChangeTariffResponse).data

          if (response.success && precheck && 'payment_method' in precheck) {
            dispatch(
              setModal({
                size: 'lg',
                children: (
                  <ConfirmModal
                    data={precheck as TariffPrecheck}
                    id={id}
                    period={period}
                    tariffName={tariffName}
                  />
                ),
              }),
            )
          } else {
            handleChangeTariff()
          }
        })
        .catch(() => handleChangeTariff())
    }
  }

  const prices = useMemo(() => {
    if (tariffs && tariffs.length) {
      return {
        annually: {
          lite: {
            price: t('pages.tariff.pricePerMonth', {
              symbol: tariffs[0].currency_symbol,
              count: tariffs[0].price_annually as unknown as number,
            }),
            discount: t('pages.tariff.pricePerMonth', {
              symbol: tariffs[0].currency_symbol,
              count: tariffs[0].price_monthly as unknown as number,
            }),
          },
          business: {
            price: t('pages.tariff.pricePerMonth', {
              symbol: tariffs[1].currency_symbol,
              count: tariffs[1].price_annually as unknown as number,
            }),
            discount: t('pages.tariff.pricePerMonth', {
              symbol: tariffs[1].currency_symbol,
              count: tariffs[1].price_monthly as unknown as number,
            }),
          },
          ...(tariffs[2] && {
            custom: {
              price: t('pages.tariff.pricePerMonth', {
                symbol: tariffs[2].currency_symbol,
                count: tariffs[2].price_annually as unknown as number,
              }),
              discount: t('pages.tariff.pricePerMonth', {
                symbol: tariffs[2].currency_symbol,
                count: tariffs[2].price_monthly as unknown as number,
              }),
            },
          }),
        },
        monthly: {
          lite: {
            price: t('pages.tariff.pricePerMonth', {
              symbol: tariffs[0].currency_symbol,
              count: tariffs[0].price_monthly as unknown as number,
            }),
            discount: t('pages.tariff.pricePerAnnually', {
              symbol: tariffs[0].currency_symbol,
              count: tariffs[0].price_annually as unknown as number,
            }),
          },
          business: {
            price: t('pages.tariff.pricePerMonth', {
              symbol: tariffs[1].currency_symbol,
              count: tariffs[1].price_monthly as unknown as number,
            }),
            discount: t('pages.tariff.pricePerAnnually', {
              symbol: tariffs[1].currency_symbol,
              count: tariffs[1].price_annually as unknown as number,
            }),
          },
          ...(tariffs[2] && {
            custom: {
              price: t('pages.tariff.pricePerMonth', {
                symbol: tariffs[2].currency_symbol,
                count: tariffs[2].price_monthly as unknown as number,
              }),
              discount: t('pages.tariff.pricePerAnnually', {
                symbol: tariffs[2].currency_symbol,
                count: tariffs[2].price_annually as unknown as number,
              }),
            },
          }),
        },
      }
    }
  }, [tariffs])

  if (!tariffs || !prices) {
    return <Preloader />
  }

  return (
    <div className={'relative h-full overflow-auto bg-[#EDF2FC] pt-[24px] pb-[40px]'}>
      <div className={'flex flex-col items-center'}>
        <p className={'text-[32px] font-medium leading-[40px]'}>{t('pages.tariff.pickPlan')}</p>
        <p className={'text-[15px] font-semibold leading-[16px]'}>
          <Trans i18nKey='pages.tariff.getDiscount'>
            <span className={'font-semibold text-default'} />
          </Trans>
        </p>
        <div className={'py-[18px]'}>
          <RadioGroup
            wrapped
            name={'period'}
            onChange={(e) => setPeriod(e.target.value as 'annually' | 'monthly')}
          >
            <RadioButton
              checked={period === 'monthly'}
              placeholder={t('fields.monthly')}
              value={'monthly'}
            />
            <RadioButton
              checked={period === 'annually'}
              placeholder={t('fields.annually')}
              value={'annually'}
            />
          </RadioGroup>
        </div>
      </div>
      <div
        className={
          'flex flex-col items-center justify-center gap-[24px] lg:flex-row lg:items-start'
        }
      >
        <div
          className={
            'order-1 mb-[5%] h-[360px] w-[100%] max-w-[320px] rounded-[24px] bg-white text-focus-500'
          }
        >
          <div className={'flex h-full flex-col px-[24px] py-[16px]'}>
            <div className={'flex flex-col gap-[16px]'}>
              <div>
                <p className={'pb-[8px] text-[24px] font-medium leading-[32px]'}>
                  {tariffs[0].name}
                </p>
                <p className={'pb-[16px] text-[15px] leading-[16px] text-focus-600 opacity-50'}>
                  {t('pages.tariff.bestForSingleBrand')}
                </p>
              </div>
              <div className={'flex flex-col gap-[4px]'}>
                <p className={'text-[20px] font-medium leading-[24px] text-default'}>
                  {prices[period].lite.price}
                </p>
                <p
                  className={`text-[15px] leading-[16px] text-focus-600 opacity-50 ${{ monthly: '', annually: 'line-through' }[period]} `}
                >
                  {prices[period].lite.discount}
                </p>
              </div>
              <div>
                {tariffDetails?.tariff?.id === tariffs[0]?.id &&
                !tariffDetails?.tariff?.is_subscription_expired &&
                tariffDetails?.tariff?.payment_periodicity === period ? (
                  <div
                    className={
                      'flex w-fit items-center gap-[8px] rounded-[8px] bg-grey-300 px-[12px] py-[11px] text-[12px] font-medium uppercase text-default'
                    }
                  >
                    <div className={'w-[16px]'}>
                      <IconCrown color={'#A059FF'} />
                    </div>
                    {t('pages.tariff.placeholders.currentPlan')}
                  </div>
                ) : (
                  <BpButton
                    loading={
                      (requestChangeTariffLoading || stripePrecheckLoading) &&
                      selectedTariff?.id === tariffs[0]?.id
                    }
                    className={'uppercase'}
                    theme={'outline'}
                    onClick={() => {
                      setSelectedTariff(tariffs[0])
                      void handleChangeTariff(tariffs[0].id, tariffs[0].name, period)
                    }}
                  >
                    {t('pages.tariff.getStarted')}
                  </BpButton>
                )}
              </div>
            </div>
            <div className={'flex-1'}>
              <div
                className={'flex h-full flex-col justify-end gap-[8px] text-[15px] leading-[16px]'}
              >
                <p className={'flex gap-[10px]'}>
                  <IconCheck className={'w-[16px]'} color={'#A059FF'} />
                  {t('pages.tariff.keywordsCount', { count: tariffs[0].max_keywords })}
                </p>
                <p className={'flex gap-[10px]'}>
                  <IconCheck className={'w-[16px]'} color={'#A059FF'} />
                  {t('pages.tariff.scanFrequencyDetailed', {
                    count: tariffs[0].daily_checks_count,
                  })}
                </p>
                <p className={'flex gap-[10px]'}>
                  <IconCheck className={'w-[16px]'} color={'#A059FF'} />
                  {t('pages.tariff.projectCount', { count: tariffs[0].max_projects })}
                </p>
                <p className={'flex gap-[10px]'}>
                  <IconCheck className={'w-[16px]'} color={'#A059FF'} />
                  {t('pages.tariff.globalCoverage')}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className={'order-0 lg:order-1'}>
          <div className={'relative w-[100%] max-w-[320px] rounded-[24px] bg-focus-600 text-white'}>
            <div
              className={
                'absolute -top-[20px] -right-[20px] rotate-[5deg] rounded-full bg-[#85FF3B] py-[9px] px-[16px] text-[20px] leading-[24px] text-focus-600'
              }
            >
              {t('pages.tariff.bestValue')}
            </div>
            <div className={'flex flex-col gap-[24px] px-[24px] py-[16px]'}>
              <div className={'flex flex-col gap-[16px]'}>
                <div>
                  <p className={'pb-[8px] text-[24px] font-medium leading-[32px]'}>
                    {tariffs[1].name}
                  </p>
                  <p className={'text-[15px] leading-[16px] opacity-50'}>
                    {t('pages.tariff.perfectForAgencies')}
                  </p>
                </div>
                <div className={'flex flex-col gap-[4px]'}>
                  <p className={'text-[20px] font-medium leading-[24px] text-default'}>
                    {prices[period].business.price}
                  </p>
                  <p
                    className={`text-[15px] leading-[16px] text-white opacity-50 ${{ monthly: '', annually: 'line-through' }[period]} `}
                  >
                    {prices[period].business.discount}
                  </p>
                </div>
                <div>
                  {tariffDetails?.tariff?.id === tariffs[1]?.id &&
                  !tariffDetails?.tariff?.is_subscription_expired &&
                  tariffDetails?.tariff?.payment_periodicity === period ? (
                    <div
                      className={
                        'flex w-fit items-center gap-[8px] rounded-[8px] bg-grey-300 px-[12px] py-[11px] text-[12px] font-medium uppercase text-default'
                      }
                    >
                      <div className={'w-[16px]'}>
                        <IconCrown color={'#A059FF'} />
                      </div>
                      {t('pages.tariff.placeholders.currentPlan')}
                    </div>
                  ) : (
                    <BpButton
                      loading={
                        (requestChangeTariffLoading || stripePrecheckLoading) &&
                        selectedTariff?.id === tariffs[1]?.id
                      }
                      className={'uppercase'}
                      onClick={() => {
                        setSelectedTariff(tariffs[1])
                        void handleChangeTariff(tariffs[1]?.id, tariffs[1].name, period)
                      }}
                    >
                      {t('pages.tariff.getStarted')}
                    </BpButton>
                  )}
                </div>
              </div>
              <div className={'flex-1'}>
                <div
                  className={
                    'flex h-full flex-col justify-end gap-[8px] text-[15px] leading-[16px]'
                  }
                >
                  <div className={'flex items-start gap-[10px]'}>
                    <IconCheck className={'w-[16px]'} color={'#85FF3B'} />
                    <p>
                      <span className={'block font-semibold'}>
                        {t('pages.tariff.keywordsCount', { count: tariffs[1].max_keywords })}
                      </span>
                      <span className={'opacity-50'}>{t('pages.tariff.brandQueriesCoverage')}</span>
                    </p>
                  </div>
                  <div className={'flex items-start gap-[10px]'}>
                    <IconCheck className={'w-[16px]'} color={'#85FF3B'} />
                    <p>
                      <span className={'block font-semibold'}>
                        {t('pages.tariff.scanFrequencyDetailed', {
                          count: tariffs[1].daily_checks_count,
                        })}
                      </span>
                      <span className={'opacity-50'}>{t('pages.tariff.blindSpotsMinimized')}</span>
                    </p>
                  </div>
                  <div className={'flex items-start gap-[10px]'}>
                    <IconCheck className={'w-[16px]'} color={'#85FF3B'} />
                    <p>
                      <span className={'block font-semibold'}>
                        {t('pages.tariff.projectsCount', { count: tariffs[1].max_projects })}
                      </span>
                      <span className={'opacity-50'}>{t('pages.tariff.multiBrandControl')}</span>
                    </p>
                  </div>
                  <div className={'flex items-start gap-[10px]'}>
                    <IconCheck className={'w-[16px]'} color={'#85FF3B'} />
                    <p>
                      <span className={'block font-semibold'}>
                        {t('pages.tariff.globalCoverage')}
                      </span>
                      <span className={'opacity-50'}>{t('pages.tariff.borderlessProtection')}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div
              className={
                'mb-[-1px] flex flex-col gap-[8px] rounded-[24px] bg-[#85FF3B] py-[16px] px-[28px] text-focus-600'
              }
            >
              <p className={'text-[15px] font-medium leading-[24px]'}>
                {t('pages.tariff.moreAdsDetected')}
              </p>
              <p className={'text-[15px] leading-[16px]'}>
                {t('pages.tariff.tariffComparisonNote')}
              </p>
            </div>
          </div>
          {
            {
              bluepear: (
                <div className={'flex flex-col gap-[8px] pt-[8px]'}>
                  <p className={'text-center text-[15px] leading-[16px] text-focus-600'}>
                    {t('pages.tariff.moneyBackGuarantee')}
                  </p>
                  <div className={'relative h-[16px] w-full overflow-hidden'}>
                    <div
                      className={
                        'absolute bottom-0 left-[-25%] w-[150%] rounded-full border-[1px] border-focus-600 pt-[150%]'
                      }
                    ></div>
                  </div>
                </div>
              ),
              robuscan: null,
              default: null,
            }[process.env.REACT_APP_PROJECT ?? 'default']
          }
        </div>
        {tariffs[2] ? (
          <div
            className={
              'order-2 mb-[5%] h-[360px] w-[100%] max-w-[320px] rounded-[24px] bg-white text-focus-500'
            }
          >
            <div className={'flex h-full flex-col px-[24px] py-[16px]'}>
              <div className={'flex flex-col gap-[16px]'}>
                <div>
                  <p className={'pb-[8px] text-[24px] font-medium leading-[32px]'}>Custom</p>
                  <p className={'pb-[16px] text-[15px] leading-[16px] text-focus-600 opacity-50'}>
                    {t('pages.tariff.customNeeds')}
                  </p>
                </div>
                <div className={'flex flex-col gap-[4px]'}>
                  <p className={'text-[20px] font-medium leading-[24px] text-default'}>
                    {prices[period].custom.price}
                  </p>
                  <p
                    className={`text-[15px] leading-[16px] text-focus-600 opacity-50 ${{ monthly: '', annually: 'line-through' }[period]} `}
                  >
                    {prices[period].custom.discount}
                  </p>
                </div>
                <div className={'flex items-center justify-between'}>
                  {tariffDetails?.tariff?.id === tariffs[2]?.id &&
                  !tariffDetails?.tariff?.is_subscription_expired &&
                  tariffDetails?.tariff?.payment_periodicity === period ? (
                    <div
                      className={
                        'flex w-fit items-center gap-[8px] rounded-[8px] bg-grey-300 px-[12px] py-[11px] text-[12px] font-medium uppercase text-default'
                      }
                    >
                      <div className={'w-[16px]'}>
                        <IconCrown color={'#A059FF'} />
                      </div>
                      {t('pages.tariff.placeholders.currentPlan')}
                    </div>
                  ) : (
                    <BpButton
                      loading={
                        (requestChangeTariffLoading || stripePrecheckLoading) &&
                        selectedTariff?.id === tariffs[2]?.id
                      }
                      className={'uppercase'}
                      theme={'outline'}
                      onClick={() => {
                        setSelectedTariff(tariffs[2])
                        void handleChangeTariff(tariffs[2].id, tariffs[2].name, period)
                      }}
                    >
                      {t('pages.tariff.getStarted')}
                    </BpButton>
                  )}
                  <button
                    className={
                      'w-fit whitespace-nowrap border-b border-dotted text-[15px] leading-[13px] text-focus'
                    }
                    onClick={() => {
                      void handleChangeTariff(0, tariffs[2].name, period)
                    }}
                  >
                    Add more keywords
                  </button>
                </div>
              </div>
              <div className={'flex-1'}>
                <div
                  className={
                    'flex h-full flex-col justify-end gap-[8px] text-[15px] leading-[16px]'
                  }
                >
                  <p className={'flex gap-[10px]'}>
                    <IconCheck className={'w-[16px]'} color={'#A059FF'} />
                    {t('pages.tariff.keywordsCount', { count: tariffs[2].max_keywords })}
                  </p>
                  <p className={'flex gap-[10px]'}>
                    <IconCheck className={'w-[16px]'} color={'#A059FF'} />
                    {t('pages.tariff.scanFrequencyDetailed', {
                      count: tariffs[2].daily_checks_count,
                    })}
                  </p>
                  <p className={'flex gap-[10px]'}>
                    <IconCheck className={'w-[16px]'} color={'#A059FF'} />
                    {t('pages.tariff.projectsCount', { count: tariffs[2].max_projects })}
                  </p>
                  <p className={'flex gap-[10px]'}>
                    <IconCheck className={'w-[16px]'} color={'#A059FF'} />
                    {t('pages.tariff.globalCoverage')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className={
              'order-2 mb-[5%] h-[360px] w-[100%] max-w-[320px] rounded-[24px] bg-white text-focus-500'
            }
          >
            <div className={'flex h-full flex-col px-[24px] py-[16px]'}>
              <div className={'flex flex-col gap-[16px]'}>
                <div>
                  <p className={'pb-[8px] text-[24px] font-medium leading-[32px]'}>
                    {t('pages.tariff.enterprisePlan')}
                  </p>
                  <p className={'pb-[16px] text-[15px] leading-[16px] text-focus-600 opacity-50'}>
                    {t('pages.tariff.customNeeds')}
                  </p>
                </div>
                <div className={'flex flex-col gap-[4px]'}>
                  <p className={'text-[20px] font-medium leading-[24px] text-default'}>
                    {t('pages.tariff.customPricing')}
                  </p>
                </div>
                <div>
                  <BpButton
                    className={'uppercase'}
                    theme={'outline'}
                    onClick={() => {
                      void handleChangeTariff(0, 'Enterprise', period)
                    }}
                  >
                    {t('pages.tariff.contactUs')}
                  </BpButton>
                </div>
              </div>
              <div className={'flex-1'}>
                <div
                  className={
                    'flex h-full flex-col justify-end gap-[8px] text-[15px] leading-[16px]'
                  }
                >
                  <p className={'flex gap-[10px]'}>
                    <IconCheck className={'w-[16px]'} color={'#A059FF'} />
                    {t('pages.tariff.unlimitedKeywords')}
                  </p>
                  <p className={'flex gap-[10px]'}>
                    <IconCheck className={'w-[16px]'} color={'#A059FF'} />
                    {t('pages.tariff.unlimitedScans')}
                  </p>
                  <p className={'flex gap-[10px]'}>
                    <IconCheck className={'w-[16px]'} color={'#A059FF'} />
                    {t('pages.tariff.unlimitedProjects')}
                  </p>
                  <p className={'flex gap-[10px]'}>
                    <IconCheck className={'w-[16px]'} color={'#A059FF'} />
                    {t('pages.tariff.globalCoverage')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <CustomerReviews />
      <Faq />
    </div>
  )
}
